/**
 * WB4 Phase 3/5 — Palette intent proposal panel (clarification + approve before ActionExecutor).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Popconfirm, Space, Tag, Typography } from 'antd';
import type { NavigateFunction } from 'react-router-dom';
import type { WorkbenchCommandContext } from '@shared/business-workbench/command-resolver';
import type { PaletteIntentProposal } from '@shared/business-workbench/workbench-palette-intent-proposal';
import { rebuildPaletteIntentProposal } from '@shared/business-workbench/workbench-palette-intent-proposal';
import {
  approvePaletteIntentProposal,
  recordPaletteIntentAiSuggested,
} from '../../utils/palette-intent-approval';
import type { WorkbenchCommandExecuteOptions } from '../../utils/globalSearchCommands';
import { css } from '@shared/utils/omitUndefined';
import styles from './PaletteIntentProposalPanel.module.css';

const { Text } = Typography;

const RISK_TIER_LABEL: Record<PaletteIntentProposal['riskTier'], string> = {
  low: '낮음',
  medium: '중간',
  high: '높음',
  finance: '재무',
};

export interface PaletteIntentProposalPanelProps {
  tenantId: string;
  proposal: PaletteIntentProposal;
  query: string;
  commandContext: WorkbenchCommandContext;
  navigate: NavigateFunction;
  businessDomain?: string;
  objectId?: string;
  executeOptions?: WorkbenchCommandExecuteOptions;
}

export function PaletteIntentProposalPanel({
  tenantId,
  proposal,
  query,
  commandContext,
  navigate,
  businessDomain,
  objectId,
  executeOptions,
}: PaletteIntentProposalPanelProps) {
  const aiSuggestedRecordedRef = useRef(false);
  const [clarification, setClarification] = useState<Partial<Record<string, string>>>({});

  useEffect(() => {
    setClarification({});
  }, [proposal.intent, query]);

  const effectiveProposal = useMemo(() => {
    if (Object.keys(clarification).length === 0) {
      return proposal;
    }
    return rebuildPaletteIntentProposal(proposal, clarification, commandContext, query);
  }, [clarification, commandContext, proposal, query]);

  useEffect(() => {
    if (aiSuggestedRecordedRef.current) {
      return;
    }
    aiSuggestedRecordedRef.current = true;
    void recordPaletteIntentAiSuggested({
      tenantId,
      proposal: effectiveProposal,
      ...(businessDomain !== undefined ? { businessDomain } : {}),
      ...(objectId !== undefined ? { objectId } : {}),
    });
  }, [businessDomain, effectiveProposal, objectId, tenantId]);

  const handleApprove = async (): Promise<void> => {
    await approvePaletteIntentProposal({
      tenantId,
      proposal: effectiveProposal,
      navigate,
      ...(businessDomain !== undefined ? { businessDomain } : {}),
      ...(objectId !== undefined ? { objectId } : {}),
      ...(executeOptions !== undefined ? { executeOptions } : {}),
    });
  };

  return (
    <section
      className={css(styles.root)}
      aria-label="AI 의도 제안"
      data-testid="palette-intent-proposal-panel"
    >
      <Space direction="vertical" size="small" className={css(styles.stack)}>
        <div className={css(styles.header)}>
          <Text strong>{effectiveProposal.label}</Text>
          <Tag color="blue">{RISK_TIER_LABEL[effectiveProposal.riskTier]}</Tag>
          {effectiveProposal.source === 'rule_match' ? (
            <Tag>규칙 매칭</Tag>
          ) : (
            <Tag color="purple">AI 분류</Tag>
          )}
        </div>
        <Text type="secondary">{effectiveProposal.interpretationSummary}</Text>
        {effectiveProposal.requiresClarification ? (
          <Alert
            type="warning"
            showIcon
            message="추가 정보가 필요합니다"
            description={
              <ul className={css(styles.missingList)} data-testid="palette-intent-missing-fields">
                {effectiveProposal.missingFields.map((field) => (
                  <li key={field.key}>
                    <label className={css(styles.clarificationField)}>
                      <span>{field.label}</span>
                      <Input
                        size="small"
                        value={clarification[field.key] ?? ''}
                        placeholder={field.label}
                        data-testid={`palette-intent-clarify-${field.key}`}
                        onChange={(event) => {
                          const value = event.target.value;
                          setClarification((current) => ({
                            ...current,
                            [field.key]: value,
                          }));
                        }}
                      />
                    </label>
                  </li>
                ))}
              </ul>
            }
          />
        ) : (
          <Popconfirm
            title={`"${effectiveProposal.command?.label ?? effectiveProposal.label}" 실행을 승인하시겠습니까?`}
            okText="승인 후 실행"
            cancelText="취소"
            onConfirm={() => void handleApprove()}
          >
            <Button
              type="primary"
              size="small"
              data-testid="palette-intent-approve"
              disabled={!effectiveProposal.command}
            >
              승인 후 실행
            </Button>
          </Popconfirm>
        )}
      </Space>
    </section>
  );
}
